import { useState } from 'react';
import { Box, Button, Modal, Text, TextArea } from '@rsos/base-components';
import {
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '@rsos/base-components/Modal/ModalComponents';
import {
  useCancelTowProRequestMutation,
  type TowProCancelRequest,
  type TowProRequestResponse,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import { extractErrorMessage } from '@rsos/reduxDataStore/src/utils/errorUtils';
import styles from './TowProCancelModal.module.css';

type RotationPlacement = TowProCancelRequest['moveCompanyTo'];

const ROTATION_OPTIONS: { value: RotationPlacement; label: string }[] = [
  { value: 'TOP', label: 'Top of the list' },
  { value: 'BOTTOM', label: 'Bottom of the list' },
];

const CANCEL_ERROR_MESSAGE =
  'Unable to cancel the tow request. Please try again.';

interface TowProCancelModalProps {
  request: TowProRequestResponse;
  showModal: boolean;
  onClose: () => void;
}

/**
 * Cancels an active tow request. Requires a cancellation reason and where the
 * released vendor re-enters the rotation list, both forwarded to TowPro.
 */
function TowProCancelModal({
  request,
  showModal,
  onClose,
}: TowProCancelModalProps): JSX.Element {
  const [cancelReason, setCancelReason] = useState('');
  const [rotationPlacement, setRotationPlacement] =
    useState<RotationPlacement | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancelTowProRequest, { isLoading }] = useCancelTowProRequestMutation();

  const canConfirm =
    cancelReason.trim().length > 0 && rotationPlacement !== null && !isLoading;

  // Reset transient state so a stale error/reason doesn't persist when the
  // modal is reopened.
  const handleClose = (): void => {
    setCancelError(null);
    setCancelReason('');
    setRotationPlacement(null);
    onClose();
  };

  const onConfirmCancel = async (): Promise<void> => {
    if (!canConfirm || rotationPlacement === null) {
      return;
    }

    setCancelError(null);
    try {
      await cancelTowProRequest({
        requestId: request.id,
        body: {
          reason: cancelReason.trim(),
          moveCompanyTo: rotationPlacement,
        },
      }).unwrap();
      handleClose();
    } catch (error) {
      // The modal stays open for a retry. etl_integrations forwards TowPro's
      // own message as `detail`; it is more actionable than the generic copy
      // when we have it.
      setCancelError(extractErrorMessage(error, CANCEL_ERROR_MESSAGE));
    }
  };

  return (
    <Modal
      id="towpro-cancel-request-modal"
      isOpened={showModal}
      onClose={handleClose}
      padding="45px 45px 35px"
      width={400}
    >
      <ModalHeader onClose={handleClose}>
        <Text color="primaryText" size="large">
          Cancel Tow Request
        </Text>
      </ModalHeader>
      <ModalBody>
        <Text color="secondaryText" size="normal">
          Reason for cancellation
        </Text>
        <Box mt={2}>
          <TextArea
            id="towpro-cancel-reason"
            name="towpro-cancel-reason"
            rows={3}
            fontSize="14px"
            value={cancelReason}
            onChange={e => setCancelReason(e.target.value)}
          />
        </Box>
        <Box mt={3}>
          <Text color="secondaryText" size="normal">
            Return the tow vendor to the rotation:
          </Text>
          <div
            className={styles.rotationOptions}
            role="radiogroup"
            aria-label="Vendor rotation placement"
          >
            {ROTATION_OPTIONS.map(option => (
              <label key={option.value} className={styles.radioLabel}>
                <input
                  type="radio"
                  name="towpro-cancel-rotation"
                  value={option.value}
                  checked={rotationPlacement === option.value}
                  onChange={() => setRotationPlacement(option.value)}
                  data-name={`towpro-cancel-rotation-${option.value.toLowerCase()}`}
                />
                {option.label}
              </label>
            ))}
          </div>
        </Box>
      </ModalBody>
      {cancelError && (
        <div className={styles.errorBanner} role="alert">
          <Text color="background" size="small">
            {cancelError}
          </Text>
        </div>
      )}
      <ModalFooter>
        <Box horizontal justify="center" width="100%">
          <Button grow btnType="tertiary" mx={0} onClick={handleClose}>
            Keep request
          </Button>
          <Button
            grow
            btnType="warning_primary"
            ml={2}
            onClick={onConfirmCancel}
            disabled={!canConfirm}
            dataName="towpro-cancel-confirm-button"
          >
            Cancel Request
          </Button>
        </Box>
      </ModalFooter>
    </Modal>
  );
}

export default TowProCancelModal;
